import pako from "pako";
import { Amplify } from "aws-amplify";
import awsExports from "../aws-exports";
import { amplifyExistingResourcesConfig, thirdPartyConfig } from "../thirdPartyConfig";
import { Auth } from "aws-amplify";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const env = awsExports.aws_content_delivery_bucket.replace("el-admin-hosting-", "");

export const configureAmplify = () => {
  Amplify.configure({
    ...awsExports,
    ...amplifyExistingResourcesConfig[env],
  });
};

export const CONTROL_ACTION_TYPE = {
  "Start Trade": "Start Trade",
  "Close Position": "Close Position",
  "New Trade": "New Trade",
};

export const MODEL_TYPE = {
  lstm: "lstm",
  transformer: "transformer",
};

export const TRADE_STATUS = {
  trading: "trading",
  waiting: "waiting",
  monitoring: "monitoring",
  positionClosed: "positionClosed",
  paused: "paused",
  closingPosition: "closingPosition",
  error: "error",
  marketClosed: "marketClosed",
  newAccount: "newAccount",
};

export const TRADING_PAIRS = [
  "BTC/USD",
  "BTC/USDC",
  "ETH/USD",
  "ETH/USDC",
  "DOT/USD",
  "DOGE/USD",
  "DOGE/USDC",
  "ADA/USD",
  "AXS/USD",
  "SOL/USD",
  "SOL/USDC",
  "1000SHIB/USD",
  "SAND/USD",
  "MANA/USD",
  "GMT/USD",
  "AVAX/USD",
  "CFX/USD",
  "XRP/USD",
  "XRP/USDC",
  "ARB/USD",
  "APT/USD",
  "ETC/USD",
  "INJ/USD",
  "BCH/USD",
  "STORJ/USD",
  "LTC/USD",
  "DYDX/USD",
  "COMP/USD",
  "MDT/USD",
  "AAVE/USD",
  "MKR/USD",
  "CELO/USD",
  "GRT/USD",
  "OP/USD",
  "DGB/USD",
  "MTL/USD",
  "APE/USD",
  "1000PEPE/USD",
  "SUI/USD",
  "EDU/USD",
  "ORDI/USD",
  "TIA/USD",
  "MEME/USD",
  "ATOM/USD",
  "GALA/USD",
  "NEAR/USD",
  "UNI/USD",
  "SEI/USD",
  "WLD/USD",
  "REEF/USD",
  "HIFI/USD",
  "1000FLOKI/USD",
  "ID/USD",
  "WIF/USD",
  "ZRX/USD",
  "PIXEL/USD",
];

export const PORTFOLIO_OPTIMIZATION_PAIRS = [
  "BTC/USD",
  "ETH/USD",
  "XRP/USD",
  "SOL/USD",
  "DOGE/USD",
  "ADA/USD",
  "BCH/USD",
  "1000SHIB/USD",
  "AVAX/USD",
  "1000FLOKI/USD",
  "1000PEPE/USD",
  "WIF/USD",
  "SUI/USD",
];

export const CME_TRADING_PAIRS = ["BTC/USD", "MBT/USD", "ETH/USD", "MET/USD"];

export const SYMBOL_TO_BALANCE_DECIMAL = {
  BNB: 3,
  USDT: 2,
  BUSD: 2,
  BTC: 5,
  ETH: 4,
};

const HIGHCHARTS_COLORS = ["#7cb5ec", "#434348", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80", "#e4d354", "#2b908f", "#f45b5b", "#91e8e1"];
// Generate TRADING_PAIR_COLORS using the Highcharts color pattern
export const TRADING_PAIR_COLORS = TRADING_PAIRS.reduce((acc, pair, index) => {
  const symbol = pair.split("/")[0];
  acc[symbol] = HIGHCHARTS_COLORS[index % HIGHCHARTS_COLORS.length];
  return acc;
}, {});

// Convert a number to a string with commas
export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function retrieveIntervalFromModelName(modelName) {
  if (modelName.startsWith("averaged_")) {
    return modelName.split("_")[1];
  }
  return modelName.split(".")[2];
}

export function retrieveIntervalInMinFromModelName(modelName) {
  if (modelName.toLowerCase().includes("averagepnl")) return modelName;
  const interval = retrieveIntervalFromModelName(modelName);
  if (interval.toLowerCase().includes("h")) {
    return parseInt(interval) * 60;
  } else if (interval.toLowerCase().includes("m")) {
    return parseInt(interval);
  } else {
    throw new Error(`invalid interval ${interval}`);
  }
}

// 给model performance页面用，用来通过model interval确定线的颜色
export function calcLineColorFromInterval(intervalInMin) {
  const colors = ["#f44336", "#03a9f4", "#8bc34a", "#2196f3", "#b71c1c", "#009688", "#00bcd4"];

  if (typeof intervalInMin === "string" && intervalInMin.includes("average")) {
    return "#333";
  }
  switch (intervalInMin) {
    case 15:
      return colors[0];
    case 20:
      return colors[1];
    case 30:
      return colors[2];
    case 45:
      return colors[3];
    case 60:
      return colors[4];
    case 90:
      return colors[5];
    case 120:
      return colors[6];
    default:
      return undefined;
  }
}

//获取前n天 年/月/日
export function getBeforeDate(n) {
  if (typeof n !== "number") return n;
  const now = new Date();
  const ntime = now.setTime(now.getTime() - n * 24 * 60 * 60 * 1000);
  return new Date(ntime).toLocaleDateString();
}

export const EXCHANGE_INSTRUMENT = {
  binance_spot: "binance_spot",
  binance_spot_algo: "binance_spot_algo",
  binance_future_algo: "binance_future_algo",
  binance_usds_future: "binance_usds_future",
  binance_coin_future: "binance_coin_future",
  binance_funding_rate_arbitrage: "binance_funding_rate_arbitrage",
  okx_usds_future: "okx_usds_future",
  okx_spot_algo: "okx_spot_algo",
  okx_funding_rate_arbitrage: "okx_funding_rate_arbitrage",
  cryptocom_usds_future: "cryptocom_usds_future",
  huobi_usdt_swap: "huobi_usdt_swap",
  huobi_coin_swap: "huobi_coin_swap",
  huobi_future: "huobi_future",
  bitflyer_lightning_fx: "bitflyer_lightning_fx",
  bitmex_perpetual: "bitmex_perpetual",
  cme_tradestation_future: "cme_tradestation_future",
  route28_usds_future: "route28_usds_future",
};

export const EXCHANGE = {
  binance: "binance",
  okx: "okx",
  cryptocom: "cryptocom",
  huobi: "huobi",
  bitflyer: "bitflyer",
  bitmex: "bitmex",
  cme: "cme",
  route28: "route28",
};

export const INSTRUMENT = {
  coin_future: "coin_future",
  usds_future: "usds_future",
  cme_future: "cme_future",
  funding_rate_arbitrage: "funding_rate_arbitrage",
  spot: "spot",
  spot_algo: "spot_algo",
  future_algo: "future_algo",
};

// Algo types for trading algorithms
export const ALGO_TYPE = {
  TWAP: "TWAP",
};

export function determineTradeTypeByExchangeInstrument(exchangeInstrument) {
  switch (exchangeInstrument) {
    case EXCHANGE_INSTRUMENT.binance_usds_future:
    case EXCHANGE_INSTRUMENT.okx_usds_future:
    case EXCHANGE_INSTRUMENT.cryptocom_usds_future:
    case EXCHANGE_INSTRUMENT.huobi_usdt_swap:
    case EXCHANGE_INSTRUMENT.bitflyer_lightning_fx:
    case EXCHANGE_INSTRUMENT.route28_usds_future: {
      return INSTRUMENT.usds_future;
    }
    case EXCHANGE_INSTRUMENT.binance_spot: {
      return INSTRUMENT.spot;
    }
    case EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage:
    case EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage: {
      return INSTRUMENT.funding_rate_arbitrage;
    }
    case EXCHANGE_INSTRUMENT.cme_tradestation_future: {
      return INSTRUMENT.cme_future;
    }
    case EXCHANGE_INSTRUMENT.binance_spot_algo:
    case EXCHANGE_INSTRUMENT.okx_spot_algo: {
      return INSTRUMENT.spot_algo;
    }
    case EXCHANGE_INSTRUMENT.binance_future_algo: {
      return INSTRUMENT.future_algo;
    }
    case EXCHANGE_INSTRUMENT.bitmex_perpetual:
    case EXCHANGE_INSTRUMENT.binance_coin_future:
    case EXCHANGE_INSTRUMENT.huobi_future:
    case EXCHANGE_INSTRUMENT.huobi_coin_swap: {
      return INSTRUMENT.coin_future;
    }
    default: {
      throw new Error(`unrecognized exchangeInstrument ${exchangeInstrument}`);
    }
  }
}

//防抖，搜索框使用
export const debounce = (fn, ms = 0) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};
export const UNGROUPED_NAME = "Ungrouped"; // 没有分组的tradeStatus的组名
/**
 * Replace instrument substring with exchange substring
 * e.g., binance_coin_future_bvi_club -> binance_bvi_club
 * @param strWithInstrument
 * @returns
 */
export function replaceInstrumentWithExchangeInStr(strWithInstrument) {
  for (let instrument in EXCHANGE_INSTRUMENT) {
    if (strWithInstrument.includes(instrument)) {
      const exchangeId = instrument.split("_")[0];
      return strWithInstrument.replace(instrument, exchangeId);
    }
  }
  throw new Error(`String ${strWithInstrument} does not contain any exchange instrument string`);
}

export function getExchangeIdFromExchangeInstrument(exchangeInstrument) {
  const exchangeId = exchangeInstrument.split("_")[0];
  for (const exchange in EXCHANGE) {
    if (exchange === exchangeId) {
      return exchangeId;
    }
  }
  throw new Error(`exchangeInstrument ${exchangeInstrument} does not contain any exchange string`);
}

const NEWRELIC_LOG_TIME_RANGE_MS = 30 * 60 * 1000;

export function generateNewRelicLink(exchangeInstrument, account, symbol) {
  const accountId = thirdPartyConfig[env].newrelic_account_id;
  const pane = {
    nerdletId: "logger.log-tailer",
    accountId,
    query: `${exchangeInstrument}-${account}-${symbol}`, // format: "binance_usds_future-bvi_sub3-BTC/USD"
  };
  const panePercentEncoded = encodeURIComponent(btoa(JSON.stringify(pane)));
  return (
    "https://one.newrelic.com/launcher/logger.log-tailer" +
    `?platform[accountId]=${accountId}` +
    `&platform[timeRange][duration]=${NEWRELIC_LOG_TIME_RANGE_MS}` +
    `&pane=${panePercentEncoded}`
  );
}

export function getAwsRegion() {
  return amplifyExistingResourcesConfig[env].aws_appsync_region;
}

export function getAwsAccountId() {
  return thirdPartyConfig[env].aws_account_id;
}

/**
 * 函数用户访问设置是PC端
 * @returns PC返回true
 * @returns boolean
 */
export function isPC() {
  const sUserAgent = navigator.userAgent.toLowerCase();
  const bIsIpad = sUserAgent.match(/ipad/i);
  const bIsIphoneOs = sUserAgent.match(/iphone os/i);
  const bIsMidp = sUserAgent.match(/midp/i);
  const bIsUc7 = sUserAgent.match(/rv:1.2.3.4/i);
  const bIsUc = sUserAgent.match(/ucweb/i);
  const bIsAndroid = sUserAgent.match(/android/i);
  const bIsCE = sUserAgent.match(/windows ce/i);
  const bIsWM = sUserAgent.match(/windows mobile/i);
  if (bIsIpad || bIsIphoneOs || bIsMidp || bIsUc7 || bIsUc || bIsAndroid || bIsCE || bIsWM) {
    return false;
  } else {
    return true;
  }
}

/**
 * Format number with thousands separator. e.g. 123456 -> 123,456
 * @return {string} a formatted string with thousands separator and maximum two decimals
 */
export function formatReadableNumber(num) {
  // only display the integer part if the decimal is less than 1% of the number
  if (num - Math.floor(num) < 0.01 * num) {
    num = Math.round(num);
  }
  return num.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
}

export function isSubAccount(tradeStatus) {
  // TODO: why AccountTab tradeStatus.exchange is cme, not cme_tradestation_future
  return (
    [INSTRUMENT.coin_future, INSTRUMENT.spot, INSTRUMENT.funding_rate_arbitrage].includes(tradeStatus.type?.toLowerCase()) ||
    (tradeStatus.parentAccount && tradeStatus.parentAccount !== tradeStatus.account)
  );
}

export const isUsdsFuture = (exchange) => {
  return [
    EXCHANGE_INSTRUMENT.binance_usds_future,
    EXCHANGE_INSTRUMENT.okx_usds_future,
    EXCHANGE_INSTRUMENT.cme_tradestation_future,
    EXCHANGE_INSTRUMENT.cryptocom_usds_future,
    EXCHANGE_INSTRUMENT.route28_usds_future,
  ].includes(exchange);
};

export const isCoinFuture = (exchange) => {
  return exchange === EXCHANGE_INSTRUMENT.binance_coin_future;
};

export const isArbitrage = (exchange) => {
  return exchange === EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage || exchange === EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage;
};

export function getExchangeInstrumentAndAccount(account) {
  const [prefix, ...suffixParts] = account.split("_");
  const suffix = suffixParts.join("_"); // Combine suffix parts back into a string

  let exchangeInstrument;
  switch (prefix) {
    case "binance":
      exchangeInstrument = EXCHANGE_INSTRUMENT.binance_usds_future;
      break;
    case "cme":
      exchangeInstrument = EXCHANGE_INSTRUMENT.cme_tradestation_future;
      break;
    case "okx":
      exchangeInstrument = EXCHANGE_INSTRUMENT.okx_usds_future;
      break;
    case "cryptocom":
      exchangeInstrument = EXCHANGE_INSTRUMENT.cryptocom_usds_future;
      break;
    case "route28":
      exchangeInstrument = EXCHANGE_INSTRUMENT.route28_usds_future;
      break;
    case "huobi":
      exchangeInstrument = EXCHANGE_INSTRUMENT.huobi_usdt_swap;
      break;
    case "bitflyer":
      exchangeInstrument = EXCHANGE_INSTRUMENT.bitflyer_lightning_fx;
      break;
    case "bitmex":
      exchangeInstrument = EXCHANGE_INSTRUMENT.bitmex_perpetual;
      break;
    default:
      exchangeInstrument = null;
  }

  return { exchangeInstrument, tradeAccount: suffix };
}

export function processAccountList(accounts) {
  const accountMap = new Map();
  accounts.forEach((account) => {
    const exchangePrefix = account.exchange.split("_")[0];
    const accountId = `${exchangePrefix}_${account.account}`;
    const parentAccountId = account.parentAccount ? `${exchangePrefix}_${account.parentAccount}` : accountId;
    if (!isSubAccount(account)) {
      if (!accountMap.has(accountId)) {
        accountMap.set(accountId, {
          usdSession: [],
          coinSession: [],
          spotSession: [],
          spotAlgoSession: [],
          futureAlgoSession: [],
          fundingRateArbitrageSession: [],
        });
      }
      let parent = accountMap.get(parentAccountId);
      switch (account.exchange) {
        case EXCHANGE_INSTRUMENT.cme_tradestation_future:
          break;
        case EXCHANGE_INSTRUMENT.cryptocom_usds_future:
          break;
        case EXCHANGE_INSTRUMENT.binance_usds_future:
          break;
        case EXCHANGE_INSTRUMENT.binance_coin_future:
          parent.coinSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_spot:
          parent.spotSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_spot_algo:
          parent.spotAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_future_algo:
          parent.futureAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage:
          parent.fundingRateArbitrageSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.okx_usds_future:
          break;
        case EXCHANGE_INSTRUMENT.okx_spot_algo:
          parent.spotAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage:
          parent.fundingRateArbitrageSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.route28_usds_future:
          break;
        default:
      }
    } else {
      let parent = accountMap.get(parentAccountId);
      if (!parent) {
        parent = {
          usdSession: [],
          coinSession: [],
          spotSession: [],
          spotAlgoSession: [],
          futureAlgoSession: [],
          fundingRateArbitrageSession: [],
        };
        accountMap.set(parentAccountId, parent);
      }

      switch (account.exchange) {
        case EXCHANGE_INSTRUMENT.binance_usds_future:
          parent.usdSession.push(`${accountId}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_coin_future:
          parent.coinSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_spot:
          parent.spotSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_spot_algo:
          parent.spotAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_future_algo:
          parent.futureAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage:
          parent.fundingRateArbitrageSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.okx_usds_future:
          parent.usdSession.push(`${accountId}`);
          break;
        case EXCHANGE_INSTRUMENT.okx_spot_algo:
          parent.spotAlgoSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage:
          parent.fundingRateArbitrageSession.push(`${account.account}`);
          break;
        case EXCHANGE_INSTRUMENT.cryptocom_usds_future:
          parent.usdSession.push(`${accountId}`);
          break;
        case EXCHANGE_INSTRUMENT.cme_tradestation_future:
          parent.usdSession.push(`${accountId}`);
          break;
        case EXCHANGE_INSTRUMENT.route28_usds_future:
          parent.usdSession.push(`${accountId}`);
          break;
        default:
          parent.usdSession.push(`${accountId}`);
          break;
      }
    }
  });
  return accountMap;
}

/**
 * Decompresses data.
 * @param {string} data - The data to decompress.
 * @returns {string} - The decompressed data if it was compressed
 */
export function decompressData(data) {
  try {
    const binaryString = atob(data); // Decode the Base64 string to a binary string
    const binData = Uint8Array.from(binaryString, (char) => char.charCodeAt(0)); // Convert the binary string to a Uint8Array
    return pako.ungzip(binData, { to: "string" });
  } catch (error) {
    throw new Error("Decompression failed: " + error.message);
  }
}

export function errorToString(error) {
  const message = error.message;
  if (message !== undefined) {
    return message;
  }
  const errorsList = error.errors;
  if (errorsList !== undefined) {
    return errorsList.map((item) => item.message).join("\n");
  }
  const errorFields = error.errorFields;
  if (errorFields !== undefined) {
    return errorFields[0].errors[0];
  }
  return String(error);
}

/**
 * Naturally sorts session names by their exchange instrument type and session number.
 * Sort order: USDS futures first, then coin futures, funding rate arbitrage, spot, and rest.
 * Within each group, sorts by session number (base accounts first, then numerically).
 *
 * @param {(string|Object)} a - First session name or object containing sessionName property
 * @param {(string|Object)} b - Second session name or object containing sessionName property
 * @returns {number} - Negative if a should come before b, positive if b should come before a, 0 if equal
 */
export const naturalSortSessions = (a, b) => {
  try {
    const strA = typeof a === "object" ? a?.sessionName || "" : String(a || "");
    const strB = typeof b === "object" ? b?.sessionName || "" : String(b || "");

    // Get instrument type
    const getType = (str) => {
      if (!str) return 0;
      if (str.includes(INSTRUMENT.usds_future)) return 5;
      if (str.includes(INSTRUMENT.coin_future)) return 4;
      if (str.includes(INSTRUMENT.funding_rate_arbitrage)) return 3;
      if (str.includes(INSTRUMENT.spot)) return 2;
      return -1;
    };

    const typeA = getType(strA);
    const typeB = getType(strB);
    if (typeA !== typeB) return typeB - typeA;

    // Extract the last continuous number from anywhere in the string
    const getLastNumber = (str) => {
      const numbers = str.match(/\d+/g); // Get all numbers
      return numbers ? parseInt(numbers[numbers.length - 1], 10) : 0; // Get last number or 0
    };

    const numberA = getLastNumber(strA);
    const numberB = getLastNumber(strB);
    return numberA - numberB;
  } catch (error) {
    console.error("Error in naturalSortSessions:", error);
    return 0;
  }
};

// return user information from cognito.
// Note that if there is no logged in user, the return value is null - not undefined. This helps effect
// hooks distinguish between login status = unknown vs. login status = not logged in.
export const getUserInfo = async () => {
  const session = await Auth.currentSession();
  const payload = session?.idToken?.payload;
  if (!payload) return null;
  // add some convenient shortcuts
  payload.username = payload["cognito:username"];
  payload.groups = payload["cognito:groups"];
  payload.userId = payload.sub; // this can be removed when we upgrade to aws-amplify v6
  return payload;
};

export const TMUX_OPERATION_DELAY = 1000; // 1s delay for tmux operations

export const formatMultiAssetConfig = (config) => {
  const defaultConfig = { ma: 30, low: 7 };
  const values = {
    ma: config?.ma ?? defaultConfig.ma,
    low: config?.low ?? defaultConfig.low,
  };

  return JSON.stringify(values);
};
